import type { ApiAccountJSON } from 'flavours/glitch/api_types/accounts';

import { detectInstanceSoftware } from '../instance_detection';

import { createDefaultInitialState } from './defaults';
import type { InitialState, UserPreferences } from './types';
import {
  getAuthData,
  fetchInstanceInfo,
  normalizeInstanceData,
  detectSearchApiVersion,
} from './utils';

export interface InstanceCapabilities {
  supportsQuotePolicy: boolean;
  supportsNotificationsV2: boolean | null;
  searchApiVersion: 2 | 1 | 0;
  // Mastodon client API version advertised by the instance (4.6 == 10).
  // 0 when unknown / not a Mastodon-compatible response.
  mastodonApiVersion: number;
}

let instanceCapabilities: InstanceCapabilities | null = null;

type FeedAccess = 'public' | 'authenticated' | 'disabled';

function normalizeFeedAccess(
  value: string | undefined,
  fallback: FeedAccess,
): FeedAccess {
  if (value === 'public' || value === 'authenticated' || value === 'disabled') {
    return value;
  }
  return fallback;
}

export async function generateInitialState(): Promise<InitialState> {
  const { domain, access_token } = getAuthData();

  const headers = {
    Authorization: `Bearer ${access_token}`,
    'Content-Type': 'application/json',
  };

  const [instanceData, credentialsData, preferencesData] = await Promise.all([
    fetchInstanceInfo(domain),
    fetch(`https://${domain}/api/v1/accounts/verify_credentials`, {
      headers,
    }).then((r) => {
      if (!r.ok) throw new Error(`Failed to verify credentials: ${r.status}`);
      return r.json() as Promise<ApiAccountJSON>;
    }),
    fetch(`https://${domain}/api/v1/preferences`, { headers })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch preferences: ${r.status}`);
        return r.json() as Promise<UserPreferences>;
      })
      .catch((): UserPreferences => ({})),
  ]);

  const normalized = normalizeInstanceData(instanceData);

  const streamingUrl = normalized.streaming_url ?? `wss://${domain}`;
  const searchApiVersion = await detectSearchApiVersion(domain, headers);
  const software = await detectInstanceSoftware(`https://${domain}`);
  const isMastodon = software === 'mastodon';
  const mastodonApiVersion = normalized.mastodon_api_version ?? 0;

  const accounts: Record<string, ApiAccountJSON> = {
    [credentialsData.id]: credentialsData,
  };

  if (normalized.contact_account) {
    accounts[normalized.contact_account.id] = normalized.contact_account;
  }

  const displayMedia = preferencesData['reading:expand:media'] ?? 'default';
  const expandSpoilers = preferencesData['reading:expand:spoilers'] ?? false;
  const autoPlayGif = preferencesData['reading:autoplay:gifs'] ?? false;
  const defaultPrivacy =
    preferencesData['posting:default:visibility'] ?? 'public';
  const defaultSensitive =
    preferencesData['posting:default:sensitive'] ?? false;
  const defaultLanguage =
    preferencesData['posting:default:language'] ?? undefined;

  const locale =
    preferencesData['posting:default:language'] ??
    normalized.languages?.[0] ??
    'en';

  const hasQuotedPolicy =
    'posting:default:quoted_policy' in preferencesData &&
    preferencesData['posting:default:quoted_policy'] !== undefined;

  instanceCapabilities = {
    supportsQuotePolicy: hasQuotedPolicy,
    supportsNotificationsV2: null,
    searchApiVersion,
    mastodonApiVersion,
  };

  const pollLimits = {
    max_options: normalized.configuration?.polls?.max_options ?? 4,
    max_option_chars:
      normalized.configuration?.polls?.max_characters_per_option ?? 50,
    min_expiration: normalized.configuration?.polls?.min_expiration ?? 300,
    max_expiration: normalized.configuration?.polls?.max_expiration ?? 2629746,
  };

  const languagesList: [string, string, string][] =
    await import('../languages').then((m) => m.LANGUAGES);

  const supportedMimeTypes =
    normalized.configuration?.media_attachments?.supported_mime_types ?? [];
  const fileExtensions = supportedMimeTypes
    .filter(
      (type) =>
        type.startsWith('image/') ||
        type.startsWith('video/') ||
        type.startsWith('audio/'),
    )
    .flatMap((type) => {
      const ext = type.split('/')[1];
      return ext ? [`.${ext}`] : [];
    });
  const acceptContentTypes = [...fileExtensions, ...supportedMimeTypes];

  const defaultContentType =
    isMastodon && !normalized.version.includes('+')
      ? 'text/plain'
      : 'text/markdown';

  const baseState = createDefaultInitialState();

  // Detected server feature flags (Mastodon 4.6 == api version 10). Used to gate
  // 4.6-only UI in standalone mode and degrade gracefully on older/other software.
  const features: string[] = [];
  if (mastodonApiVersion >= 10) {
    features.push('collections');
  }

  const liveLocal = normalizeFeedAccess(
    normalized.live_feeds?.local,
    'authenticated',
  );
  const liveRemote = normalizeFeedAccess(
    normalized.live_feeds?.remote,
    'authenticated',
  );
  const topicLocal = normalizeFeedAccess(
    normalized.topic_feeds?.local,
    'authenticated',
  );
  const topicRemote = normalizeFeedAccess(
    normalized.topic_feeds?.remote,
    'authenticated',
  );

  const initialState: InitialState = {
    ...baseState,
    meta: {
      ...baseState.meta,
      access_token,
      domain: normalized.domain,
      locale,
      streaming_api_base_url: streamingUrl,
      me: credentialsData.id,
      owner: normalized.contact_account?.id,
      title: normalized.title,
      version: normalized.version,
      limited_federation_mode: normalized.limited_federation ?? false,
      registrations_open: normalized.registrations_enabled,
      search_enabled: searchApiVersion > 0,
      trends_enabled: isMastodon,
      terms_of_service_enabled: isMastodon,
      local_live_feed_access: liveLocal,
      remote_live_feed_access: liveRemote,
      local_topic_feed_access:
        topicLocal === 'disabled' ? 'authenticated' : topicLocal,
      remote_topic_feed_access: topicRemote,
      admin: normalized.contact_account?.id ?? '',
      auto_play_gif: autoPlayGif,
      display_media: displayMedia,
      expand_spoilers: expandSpoilers,
      status_page_url: normalized.status_page_url ?? '',
      default_content_type: defaultContentType,
      software,
      mastodon_api_version: mastodonApiVersion,
    },
    accounts,
    features,
    languages: languagesList,
    poll_limits: pollLimits,
    compose: {
      me: credentialsData.id,
      default_privacy: defaultPrivacy,
      default_sensitive: defaultSensitive,
      default_language: defaultLanguage,
      ...(hasQuotedPolicy && {
        default_quote_policy:
          preferencesData['posting:default:quoted_policy'] ?? 'public',
      }),
      text: '',
    },
    media_attachments: {
      accept_content_types: acceptContentTypes,
    },
    settings: baseState.settings,
  };

  return initialState;
}

export {
  saveInitialState,
  loadInitialState,
  clearInitialState,
} from './storage';
export type { InitialState } from './types';

export function getInstanceCapabilities(): InstanceCapabilities {
  if (!instanceCapabilities) {
    return {
      supportsQuotePolicy: false,
      supportsNotificationsV2: null,
      searchApiVersion: 0,
      mastodonApiVersion: 0,
    };
  }
  return instanceCapabilities;
}

export function setNotificationsV2Support(supported: boolean) {
  if (instanceCapabilities) {
    instanceCapabilities.supportsNotificationsV2 = supported;
  }
}
